import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    Popover,
    Divider
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs from 'dayjs';
import { getActionableStatusStyle } from '../../Utility/apiHelper';
import ActionableDetailCard from '../ActionableDetailCard';
import BiztantuLoader from '../Loader';
import closeIcon from '../../Assets/images/close_red.png';
import './ActionablesCalendar.css';
import {
    handleValidateCodevalue,
    ACTIONABLES_CALENDAR_ENTITY_CONFIG,
    ACTIONABLES_CALENDAR_FUTURE_MONTHS_LIMIT,
    ACTIONABLES_CALENDAR_MAX_SPAN_TEXT_LENGTH,
    ACTIONABLES_CALENDAR_MAX_VISIBLE_SLOTS,
    ACTIONABLES_CALENDAR_PAST_MONTHS_LIMIT,
    ACTIONABLES_CALENDAR_PILL_BG_OPACITY,
    ACTIONABLES_CALENDAR_PILL_HOVER_OPACITY,
    ACTIONABLES_CALENDAR_WEEK_DAYS
} from '../../Utility/constant';
import {
    getActionableEventCssVars,
    groupActionablesByDate,
    buildCalendarMonthGrid,
    isCalendarPrevDisabled,
    isCalendarNextDisabled,
    truncateText,
    isToday
} from '../../Utility/calculations';

const INITIAL_POPOVER_STATE = { anchor: null, items: [], date: null };

const ActionablesCalendar = ({
    data = [],
    internationalise,
    isLead = true,
    handleOnClickCard,
    isLoading = false
}) => {
    const entityConfig = ACTIONABLES_CALENDAR_ENTITY_CONFIG[isLead ? 'lead' : 'task'];
    const [currentDate, setCurrentDate] = useState(() => dayjs().startOf('month'));
    const [popoverState, setPopoverState] = useState(INITIAL_POPOVER_STATE);

    const handlePrevMonth = () => {
        if (!isCalendarPrevDisabled(currentDate, ACTIONABLES_CALENDAR_PAST_MONTHS_LIMIT)) {
            setCurrentDate(currentDate.subtract(1, 'month'));
        }
    };

    const handleNextMonth = () => {
        if (!isCalendarNextDisabled(currentDate, ACTIONABLES_CALENDAR_FUTURE_MONTHS_LIMIT)) {
            setCurrentDate(currentDate.add(1, 'month'));
        }
    };

    const handleOpenMorePopover = (event, dateStr, items) => {
        event.stopPropagation();
        setPopoverState({ anchor: event.currentTarget, items, date: dateStr });
    };

    const handleClosePopover = () => {
        setPopoverState(INITIAL_POPOVER_STATE);
    };

    // Normalize raw data into items with valid start/end date ranges
    const normalizedData = useMemo(() => {
        if (!data || !Array.isArray(data)) return [];

        return data.map(item => {
            let startDateStr;
            if (item[entityConfig.createdDateField]) {
                startDateStr = dayjs(item[entityConfig.createdDateField]).format('YYYY-MM-DD');
            } else if (item.createdAt) {
                startDateStr = dayjs(item.createdAt).format('YYYY-MM-DD');
            } else {
                startDateStr = dayjs().format('YYYY-MM-DD');
            }

            const todayStr = dayjs().format('YYYY-MM-DD');
            let endDateStr;
            if (item.nextFollowupDate) {
                if (isToday(item.nextFollowupDate)) {
                    endDateStr = todayStr;
                } else {
                    endDateStr = dayjs(item.nextFollowupDate).format('YYYY-MM-DD');
                }
            } else {
                endDateStr = startDateStr;
            }

            if (endDateStr < startDateStr) {
                endDateStr = startDateStr;
            }

            const rawStatus = item[entityConfig.statusField];
            const systemLookup = item.systemLookUpCodeStatus;
            const statusStyle = getActionableStatusStyle(entityConfig.statusType, systemLookup);

            const leadFullName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
            const subtitleSource = entityConfig.isLead
                ? leadFullName || entityConfig.defaultSubtitle
                : item.description || item.customerName || entityConfig.defaultSubtitle;
            const entityId = item[entityConfig.idField];

            return {
                id: `${entityConfig.statusType}-${entityId}`,
                rawId: entityId,
                startDate: startDateStr,
                endDate: endDateStr,
                subtitleText: truncateText(subtitleSource, ACTIONABLES_CALENDAR_MAX_SPAN_TEXT_LENGTH),
                statusStyle,
                rawStatus,
                systemLookup,
                rawItem: item
            };
        });
    }, [data, entityConfig]);

    // Group items into each day from startDate to endDate (inclusive)
    const itemsByDate = useMemo(() => {
        return groupActionablesByDate(normalizedData);
    }, [normalizedData]);

    // Build the month grid (week rows of 7 days)
    const monthGrid = useMemo(() => {
        return buildCalendarMonthGrid(currentDate);
    }, [currentDate]);

    const isPrevDisabled = useMemo(() => {
        return isCalendarPrevDisabled(currentDate, ACTIONABLES_CALENDAR_PAST_MONTHS_LIMIT);
    }, [currentDate]);

    const isNextDisabled = useMemo(() => {
        return isCalendarNextDisabled(currentDate, ACTIONABLES_CALENDAR_FUTURE_MONTHS_LIMIT);
    }, [currentDate]);

    return (
        <BiztantuLoader open={isLoading} isChildren position="absolute">
            <Box className="actionables-calendar">
                {/* Header Controls */}
                <Box className="actionables-calendar__header">
                    <Typography variant="h6" className="actionables-calendar__month">
                        {currentDate.format('MMMM YYYY')}
                    </Typography>

                    <Box className="actionables-calendar__controls">
                        <Tooltip title={isPrevDisabled ? '' : (handleValidateCodevalue(internationalise, 'DB_CAL_PREV_MONTH') || 'Previous month')}>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handlePrevMonth}
                                    disabled={isPrevDisabled}
                                    className="actionables-calendar__nav-button"
                                >
                                    <ChevronLeftIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={isNextDisabled ? '' : (handleValidateCodevalue(internationalise, 'DB_CAL_NEXT_MONTH') || 'Next month')}>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handleNextMonth}
                                    disabled={isNextDisabled}
                                    className="actionables-calendar__nav-button"
                                >
                                    <ChevronRightIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Weekday Header */}
                <Box className="actionables-calendar__weekdays">
                    {ACTIONABLES_CALENDAR_WEEK_DAYS.map((dayName, idx) => {
                        const isWeekend = idx === 0 || idx === 6;
                        return (
                            <Typography
                                key={dayName}
                                variant="caption"
                                className={`actionables-calendar__weekday${isWeekend ? ' actionables-calendar__weekday--weekend' : ''}`}
                            >
                                {dayName}
                            </Typography>
                        );
                    })}
                </Box>

                {/* Calendar Grid Weeks */}
                <Box className="actionables-calendar__grid">
                    {monthGrid.map((week, weekIdx) => (
                        <Box
                            key={`week-${weekIdx}`}
                            className="actionables-calendar__week"
                        >
                            {week.map((day) => {
                                const dayItems = itemsByDate[day.dateStr] || [];
                                const visibleItems = dayItems.slice(0, ACTIONABLES_CALENDAR_MAX_VISIBLE_SLOTS);
                                const overflowCount = dayItems.length - visibleItems.length;

                                return (
                                    <Box
                                        key={day.dateStr}
                                        className={`actionables-calendar__day${!day.isCurrentMonth ? ' actionables-calendar__day--other-month' : day.isWeekend ? ' actionables-calendar__day--weekend' : ''}`}
                                    >
                                        {/* Center-Aligned Day Number */}
                                        <Box className="actionables-calendar__day-header">
                                            <Typography
                                                className={`actionables-calendar__day-number${day.isToday ? ' actionables-calendar__day-number--today' : !day.isCurrentMonth ? ' actionables-calendar__day-number--other-month' : ''}`}
                                            >
                                                {day.dayNumber}
                                            </Typography>
                                        </Box>

                                        {/* Discrete Actionable Pills */}
                                        <Box className="actionables-calendar__day-events">
                                            {visibleItems.map((item) => {
                                                const pillCssVars = getActionableEventCssVars(
                                                    item.statusStyle,
                                                    ACTIONABLES_CALENDAR_PILL_BG_OPACITY,
                                                    ACTIONABLES_CALENDAR_PILL_HOVER_OPACITY
                                                );

                                                return (
                                                    <Tooltip
                                                        key={`day-pill-${item.id}-${day.dateStr}`}
                                                        title={
                                                            <ActionableDetailCard
                                                                item={item}
                                                                isLead={entityConfig.isLead}
                                                                internationalise={internationalise}
                                                                onClick={handleOnClickCard}
                                                            />
                                                        }
                                                        arrow
                                                        placement="top"
                                                        componentsProps={{
                                                            tooltip: {
                                                                className: 'actionables-calendar__tooltip'
                                                            }
                                                        }}
                                                    >
                                                        <Box
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (handleOnClickCard) {
                                                                    handleOnClickCard(item.rawItem);
                                                                }
                                                            }}
                                                            className="actionables-calendar__event"
                                                            style={pillCssVars}
                                                        >
                                                            <Box className="actionables-calendar__event-content">
                                                                <Typography
                                                                    component="span"
                                                                    className="actionables-calendar__event-id"
                                                                >
                                                                    {item.rawId}
                                                                </Typography>
                                                                <Typography
                                                                    component="span"
                                                                    className="actionables-calendar__event-title"
                                                                >
                                                                    {item.subtitleText}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </Tooltip>
                                                );
                                            })}

                                            {/* +N More Overflow Chip */}
                                            {overflowCount > 0 && (
                                                <Box className="actionables-calendar__more">
                                                    <Typography
                                                        variant="caption"
                                                        onClick={(e) => handleOpenMorePopover(e, day.dateStr, dayItems)}
                                                        className="actionables-calendar__more-button"
                                                    >
                                                        +{overflowCount} {handleValidateCodevalue(internationalise,'COMMON_SHOW_MORE')}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    ))}
                </Box>

                {/* Overflow Day Popover with Close Button */}
                <Popover
                    open={Boolean(popoverState.anchor)}
                    anchorEl={popoverState.anchor}
                    onClose={handleClosePopover}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    PaperProps={{
                        className: 'actionables-calendar__popover'
                    }}
                >
                    <Box className="actionables-calendar__popover-header">
                        <Typography variant="subtitle2" className="actionables-calendar__popover-title">
                            {`${handleValidateCodevalue(internationalise, isLead ? "DB_ACTIONABLES_LEAD_ON" : "DB_ACTIONABLES_TASK_ON") || "Actionables on"} ${popoverState.date ? dayjs(popoverState.date).format('MMM D, YYYY') : ''}`}
                        </Typography>
                        <IconButton
                            size="small"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleClosePopover();
                            }}
                            className="actionables-calendar__popover-close"
                            aria-label="close"
                        >
                            <img src={closeIcon} alt="close" style={{ width: 18, height: 18 }} />
                        </IconButton>
                    </Box>
                    <Divider className="actionables-calendar__popover-divider" />
                    <Box className="actionables-calendar__popover-list">
                        {popoverState.items.map(item => (
                            <ActionableDetailCard
                                key={`popover-card-${item.id}`}
                                item={item}
                                isLead={entityConfig.isLead}
                                internationalise={internationalise}
                                onClick={(raw) => {
                                    if (handleOnClickCard) {
                                        handleOnClickCard(raw);
                                    }
                                }}
                            />
                        ))}
                    </Box>
                </Popover>
            </Box>
        </BiztantuLoader>
    );
};

export default ActionablesCalendar;
